/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react';
import isempty from 'lodash.isempty';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {EventCodeValueType, getEventName} from '@ion/core/src/services/EventCodes';
import {GenericObject, HVSRecord} from '@ion/core/src/types/entities';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import useRootManager from '../../client/useRootManager';
import EventDetailPopup, {popupContentStyle} from '../components/EventDetailPopup';
import EventPopup from '../components/EventPopup';
import {
  BACKGROUND_NODE_LAYER,
  BACKGROUND_PATH_LAYER,
  BACKGROUND_WORLD_LAYER,
  END_EVENT_NODE_LAYER,
  FleetLayerProps,
  IN_TRIP_NODE_LAYER,
  IN_TRIP_PATH_LAYER,
  TRIP_ASSET_LAYER_ID
} from '../constants';
import {
  getIconSizeLabelByZoom,
  hitTest,
  layerExists,
  onSelect,
  removeLayers,
  VERSION
} from '../helpers/MapViewUtils';
import useAssetHistoryController from '../hooks/useAssetHistoryController';
import useAssetHistoryMapLayers from '../hooks/useAssetHistoryMapLayers';
import {useAssetSpeed} from '../hooks/useAssetSpeed';
import {VIEW_ZOOM} from './AssetLayer';
import {isHistoryEnabled} from './LayerChooser';

type AssetHistoryLayer = FleetLayerProps & {
  boundaryProps: any;
  commonProps: any;
};

/**
 * AssetHistoryLayerV2
 *
 * Manages the full lifecycle of rendering and interacting with asset history on the map.
 *
 * Responsibilities:
 * - Initializes and renders history layers (trip, background, and event nodes)
 * - Handles map interactions (click, hover, zoom) and synchronizes them with UI state
 * - Applies filtering, grouping, and selection logic based on hovered or selected events
 * - Dynamically updates layer renderers based on zoom level
 * - Controls popup behavior, rendering different content for live/tracking vs static modes
 * - Coordinates timeline, tracking, and snap-to-road behaviors
 * - Maintains internal refs to avoid unnecessary re-renders and ensure performant updates
 *
 * Behavior Overview:
 * - When history data or settings change, layers are rebuilt and re-initialized
 * - Hovering or clicking events updates map focus, grouping, and popup content
 * - Feature filters are applied across layers unless a specific event is active
 * - In live/tracking mode, interactions rely on hitTest and map events instead of static filtering
 * - Popups are rendered via a React container and anchored to map graphics
 *
 * Notes:
 * - Uses refs extensively to keep mutable state outside React render cycles
 * - Separates concerns via custom hooks (AHR utilities, feature layers, speed, etc.)
 * - Designed for scalability and reuse across different history/report scenarios
 */
const AssetHistoryLayerV2 = ({
  map,
  view,
  layerProps,
  boundaryProps,
  commonProps
}: AssetHistoryLayer) => {
  let container;

  const {
    assetsRoutesHistory,
    hoveredEvent,
    historyUpdatesLive,
    onEventHovered,
    setHistorySelectedAsset,
    historyReportType,
    isPreferenceRefresh,
    featureFilter,
    isAssetSnapToRoadEnabled
  } = layerProps;

  const {
    layers,
    isAssetHistory,
    isTracking,
    onLayerDrawn,
    dataState,
    timeline,
    toggleTimeSlider,
    hasIOnVision,
    setIONVision,
    streetViewClick,
    isTripServicesEnabled,
    onTripHistoryMarkerSelect,
    isSelectionEnabled = false
  } = commonProps;

  const {boundary}                                    = boundaryProps;
  const isMounted                                     = useHasMounted();
  const {render, createContainer}                     = useRootManager();
  const [zoom, setZoom]                               = useState(VIEW_ZOOM);
  const [zoomInitialized, setZoomInitialized]         = useState(false);
  const [hoveredPopupGraphic, setHoveredPopupGraphic] = useState(null);
  const isMountedRef                                  = useRef(isMounted);
  const isAssetPointClicked                           = useRef(false);
  const mapPannedRef                                  = useRef(false);
  const layersRef                                     = useRef(layers);
  const isHistoryRef                                  = useRef(isTracking);
  const reportRef                                     = useRef(historyReportType);
  const historyUpdatesLiveRef                         = useRef(historyUpdatesLive);
  const hoveredEventRef                               = useRef(hoveredEvent);
  const mapClickEventHandlerRef                       = useRef<__esri.Handle | null>(null);
  const mapZoomEventHandlerRef                        = useRef<__esri.Handle | null | undefined>(null);
  const getSpeed                                      = useAssetSpeed();

  const {
    moveMapView,
    isHistoryVisible,
    updateClickedGroup,
    onUpdateGroupedView,
    getAllAssetsHistory,
    getAllRoutesHistory,
    checkLayersDrawnComplete,
    insertAssetsRoutesIntoLayer
  } = useAssetHistoryController({
    map,
    zoom,
    mapView : view,
    mapPannedRef,
    onLayerDrawn,
    featureFilter,
    isSelectionEnabled
  });

  const {
    graphicLayerRef,
    preventZoomRef,
    assetsPointsRef,
    iconSizeLabelRef,
    assignLayerRenderer,
    initializeAssetsOnMap,
    createBackgroundLayer,
    applyFilterToLayerViews,
    backgroundWorldLayerRef,
    endEventNodeRendererRef,
    largeIconSizeRendererRef,
    normalIconSizeRendererRef,
    mediumIconSizeRendererRef,
    endEventNodeOffsetRendererRef
  } = useAssetHistoryMapLayers({
    map,
    zoom,
    mapPannedRef,
    onLayerDrawn,
    featureFilter,
    mapView : view,
    isSelectionEnabled
  });

  const allAssetsHistory = useMemo(() => getAllAssetsHistory(assetsRoutesHistory), [assetsRoutesHistory]);
  const allRoutesHistory = useMemo(() => getAllRoutesHistory(assetsRoutesHistory), [assetsRoutesHistory]);

  if (isMounted && !container) {
    container = createContainer('trip-history-popup-container');
  };

  useEffect(() => {
    if (isMounted) {
      isMountedRef.current = isMounted;
    };
  }, [isMounted]);

  useEffect(() => {
    if (dataState === 'processed') {
      checkLayersDrawnComplete();
    };
  }, [dataState]);

  useEffect(() => {
    if (timeline?.isTimeSliderOn && backgroundWorldLayerRef.current) {
      backgroundWorldLayerRef.current.visible = !timeline.isTimeSliderOn;
    }
  }, [timeline]);

  useEffect(() => {
    if (graphicLayerRef.current) {
      (graphicLayerRef.current as any).visible = isHistoryVisible(layers);
    }
    layersRef.current = layers;
  }, [layers]);
  
  useEffect(() => {
    reportRef.current = historyReportType;
  }, [historyReportType]);
  
  useEffect(() => {
    isHistoryRef.current = isTracking;
  }, [isTracking]);
  
  useEffect(() => {
    historyUpdatesLiveRef.current = historyUpdatesLive;
  }, [historyUpdatesLive]);

  useEffect(() => {
    hoveredEventRef.current = hoveredEvent;
    applyReportFilter(hoveredEvent, historyUpdatesLiveRef.current);
  }, [hoveredEvent]);

  useEffect(() => {
    if (isMountedRef.current) {
      removeLayers(map,
        [
          IN_TRIP_NODE_LAYER,
          IN_TRIP_PATH_LAYER,
          BACKGROUND_NODE_LAYER,
          BACKGROUND_PATH_LAYER,
          END_EVENT_NODE_LAYER
        ]
      );
      view?.popup?.close();
      preventZoomRef.current = true;
      insertAssetsRoutesIntoLayer({
        returnAllData : true,
        callback      : initializeHistory,
        assetsRoutesHistory
      });
    }
  }, [isAssetSnapToRoadEnabled]);
  
  useEffect(() => {
    if (!isempty(assetsRoutesHistory)) {
      // every asset loaded so far is drawn only while the History layers are
      // not on the map yet - a first draw, or a new report after they were
      // cleared. Once they are, each page adds only its own asset: drawing
      // them all again added another copy of every earlier asset's graphics
      // with each page, and a report over many assets ran the browser out of
      // memory before it finished loading ...
      const drawAllAssets = !layerExists(map, IN_TRIP_NODE_LAYER);
      !isTracking && view?.popup?.close();
      insertAssetsRoutesIntoLayer({
        isFirstRender : drawAllAssets,
        callback      : initializeHistory,
        assetsRoutesHistory
      });
    }
  }, [assetsRoutesHistory]);

  /**
   * Syncs layer filters with the current feature filter.
   * - Applies the filter to all relevant layers when no event is hovered
   * - Re-applies report filtering when a hovered event exists
   */
  useEffect(() => {
    if (!hoveredEventRef.current) {
      applyFilterToLayerViews(featureFilter);
    } else {
      applyReportFilter(hoveredEventRef.current, historyUpdatesLiveRef.current, true);
    }
  }, [featureFilter]);

  /**
   * Sets up map click handling for live/tracking modes and performs cleanup on unmount.
   * - Registers click handler with hitTest for asset selection
   * - Clears selected history asset when applicable
   * - Cleans up event listeners and renderer references on unmount
   */
  useEffect(() => {
    if (historyUpdatesLive || isTracking) {
      const layerId = (historyUpdatesLive || isTracking) ? TRIP_ASSET_LAYER_ID : IN_TRIP_NODE_LAYER;
      mapClickEventHandlerRef.current = view.on('click', function({x, y}) {
        hitTest({x, y}, {view, layerId, handler, includeAny: true});
        if (isHistoryEnabled(layersRef.current) && isHistoryRef.current) {
          setHistorySelectedAsset?.(null);
        }
      });
    }
    return function() {
      mapClickEventHandlerRef.current?.remove();
      mapZoomEventHandlerRef.current?.remove();
      normalIconSizeRendererRef.current     = null;
      mediumIconSizeRendererRef.current     = null;
      largeIconSizeRendererRef.current      = null;
      endEventNodeRendererRef.current       = null;
      endEventNodeOffsetRendererRef.current = null;
      iconSizeLabelRef.current              = null;
    };
  }, []);

  /**
   * Controls popup rendering and visibility based on hovered graphic state.
   * - Renders appropriate popup content depending on live/tracking mode
   * - Updates popup styling dynamically
   * - Opens popup at the selected graphic location with a slight delay
   * - Closes popup when no valid hovered graphic is present or during preference refresh
   */
  useEffect(() => {
    if (hoveredPopupGraphic && !isPreferenceRefresh) {
      const {selectedAsset, selectedGraphics} = hoveredPopupGraphic;
      const speed     = getSpeed(selectedAsset, selectedAsset.speed);
      const eventName = getEventName(
        {from: EventCodeValueType.eventCode, val: selectedAsset.eventCode}
      );
      if (historyUpdatesLive || isTracking) {
        container.style.cssText = popupContentStyle;
        selectedAsset && render(
          <EventDetailPopup eventName={eventName}
            asset={selectedAsset}
            setIONVision={setIONVision}
            hasIOnVision={hasIOnVision} />,
          container
        );
      } else {
        container.style.cssText = 'display:flex;justify-content:center;align-items:center;';
        selectedAsset && render(
          <EventPopup setIONVision={setIONVision}
            hasIOnVision={hasIOnVision}
            streetViewClick={streetViewClick}
            selectedAsset={{...selectedAsset, isPreferenceRefresh}}
            skipSpeed={isEmpty(String(speed))} />,
          container
        );
      }
      const timer = setTimeout(() => {
        view.popup.open({
          location : selectedGraphics.geometry,
          content  : container
        });
        clearTimeout(timer);
      }, 250);
    } else {
      view.popup.close();
    }
  }, [hoveredPopupGraphic, isPreferenceRefresh]);

  /**
   * Delegates trip history marker selection if a handler is provided.
   */
  const handler = (item: HVSRecord, graphic: __esri.Graphic, coordinates: any) => {
    if (onTripHistoryMarkerSelect) {
      onTripHistoryMarkerSelect(item, graphic, coordinates);
    }
  };

  /**
   * Handles showing a popup when hovering an asset on the map.
   * - Finds the corresponding graphic by OBJECTID
   * - Sets the popup state with both asset data and graphic
   * - Moves the map view to the hovered asset location
   * - Clears the popup if the event is invalid or grouped
   */
  const onShowPopup = hoveredEvent => {
    const current = assetsPointsRef.current || graphicLayerRef.current;
    if (current && !hoveredEvent?.groupRowData) {
      const graphic = current?.graphics?.find(({attributes}) =>
        attributes?.messageUuid === hoveredEvent?.messageUuid);
      if (graphic) {
        setHoveredPopupGraphic({selectedAsset: hoveredEvent, selectedGraphics: graphic});
        const {latitude, longitude} = hoveredEvent;
        moveMapView([[longitude, latitude]], {longitude, latitude});
      }
    } else {
      setHoveredPopupGraphic(null);
    }
  };

  /**
   * Applies report filtering based on the hovered event.
   * - Updates grouped view and clicked group state
   * - Optionally triggers popup display and map zoom
   * - Toggles background layer visibility based on selection
   * - Skips execution when live history updates are enabled
   */
  const applyReportFilter = (
    hoveredEvent: HVSRecord | null,
    historyUpdatesLive: boolean,
    skipMapZooming: boolean = false
  ) => {
    mapPannedRef.current = false;
    if (!historyUpdatesLive) {
      const hasEvent = !!hoveredEvent;
      updateClickedGroup({
        hoveredEvent : hasEvent ? hoveredEvent : null,
        isTripServicesEnabled,
        map,
        view
      });
      onUpdateGroupedView({
        assets       : getAllAssetsHistory(assetsRoutesHistory),
        hoveredEvent : hasEvent ? hoveredEvent : null,
        setZoomInitialized,
        skipMapZooming
      });
      if (hasEvent) {
        onShowPopup(hoveredEvent);
      } else {
        setHoveredPopupGraphic(null);
      }
      removeLayers(map, [BACKGROUND_WORLD_LAYER]);
      backgroundWorldLayerRef.current = null;
      
      if (hoveredEvent) {
        createBackgroundLayer(isSelectionEnabled);
      }

      if (backgroundWorldLayerRef.current) {
        backgroundWorldLayerRef.current.visible = hasEvent;
      }
    }
  };

  /**
   * Handles item click interactions on the map.
   * - Marks the event as coming from a map click
   * - Updates hovered state and popup graphic
   * - Tracks asset selection for trip layers
   * - Clears selection when clicking outside (if selection is disabled)
   */
  const onItemClick = (item: HVSRecord, graphic: __esri.Graphic) => {
    if (item && graphic) {
      onEventHovered({
        ...item,
        isMapClick     : true,
        isNonTripEvent : isAssetHistory ? !item.tripGroup : false
      } as any);
      setHoveredPopupGraphic({
        selectedAsset    : item,
        selectedGraphics : graphic
      });
      isAssetPointClicked.current = true;
    } else if (!isSelectionEnabled) {
      onEventHovered(null);
    }
    if (graphic?.layer?.id === TRIP_ASSET_LAYER_ID) {
      setHistorySelectedAsset?.(item);
    }
  };
  
  /**
   * Configures map click and zoom behavior.
   * - Watches zoom level to update state and renderer dynamically
   * - Ensures minimum zoom threshold
   * - Initializes zoom state when applicable
   * - Updates layer renderer only when icon size changes
   * - Registers selection handler for map interactions
   */
  const setClickSetting = layerId => {
    mapZoomEventHandlerRef.current = view?.watch('zoom', zoomVal => {
      setZoom(zoomVal > VIEW_ZOOM ? zoomVal : VIEW_ZOOM);
      if (!zoomInitialized && !historyUpdatesLive) {
        setZoomInitialized(true);
      }
      const iconSizeLabel = getIconSizeLabelByZoom(zoomVal as number);
      if (Number.isInteger(zoomVal) && iconSizeLabelRef.current !== iconSizeLabel) {
        iconSizeLabelRef.current = iconSizeLabel;
        assignLayerRenderer(zoomVal);
      }
    });
    onSelect({view, layerId, handler: onItemClick, includeAny: true, hitTestVersion: VERSION.V2});
  };
  
  /**
   * Initializes the asset history layers and handles map state management.
   * - Removes existing history layers from the map
   * - Toggles time slider visibility based on timeline state
   * - Sets up asset history layers with initial data and configuration
   * - Manages zoom levels and map boundaries for proper rendering
   */
  const initializeHistory = async(assetsHistory: HVSRecord[], routesHistory: GenericObject) => {
    timeline?.isTimeSliderOn && toggleTimeSlider();
    await initializeAssetsOnMap({
      map       : map,
      mapView   : view,
      assets    : assetsHistory,
      zoomLevel : VIEW_ZOOM,
      boundary,
      setZoom,
      isTracking,
      routesHistory,
      isAssetHistory,
      hoveredEventRef,
      setClickSetting,
      allAssetsHistory,
      allRoutesHistory,
      applyReportFilter,
      historyUpdatesLive,
      historyUpdatesLiveRef,
      isTripServicesEnabled,
      isAssetSnapToRoadEnabled
    });
  };

  return null;
};

export default forwardRef(AssetHistoryLayerV2);
